import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Package, Plus, Search, X, Check } from 'lucide-react';
import { apiService } from '@/lib/api';
import { useModalLayer, LayerDepthContext } from '@/lib/layerManager';
import { useDebounce } from '@/hooks/useDebounce';
import { LoadingSpinner } from './LoadingSpinner';
import type { CatalogItemDTO } from '@/types';

const PRICE_BOOK_PAGE_SIZE = 50;
// Matches Tailwind's `md` breakpoint, where the dialog switches to its two-column layout.
const PHONE_WIDTH_QUERY = '(max-width: 767px)';

/** True below the `md` breakpoint, where the item editor uses its phone layout. */
export function usePhoneWidth(): boolean {
    const [matches, setMatches] = useState(
        () => typeof window !== 'undefined' && window.matchMedia(PHONE_WIDTH_QUERY).matches,
    );
    useEffect(() => {
        const mq = window.matchMedia(PHONE_WIDTH_QUERY);
        const onChange = () => setMatches(mq.matches);
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);
    return matches;
}

interface ItemEditorDialogProps {
    title: string;
    onClose: () => void;
    /** The item form fields and anything shown under them. */
    children: React.ReactNode;
    /** Cancel / save buttons, pinned under the form. Leave out for none (closing is then the X). */
    footer?: React.ReactNode;
    /** Shown under the form on wider screens, and under the Price Book on phones. */
    summary?: React.ReactNode;
    /** Price Book column; omitted when there's no company to load a catalog for. */
    priceBook?: React.ReactNode;
    /** Header icon; defaults to the item box. */
    icon?: React.ElementType;
}

/**
 * Floating item editor: the form on the left, the Price Book on the right. On phones it's a
 * bottom sheet with the Price Book under the form and the buttons pinned to the bottom.
 */
export function ItemEditorDialog({ title, onClose, children, footer, summary, priceBook, icon: Icon = Package }: ItemEditorDialogProps) {
    const layer = useModalLayer({ enabled: true, onClose });

    const closeLayer = layer.onClose;
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            // The name field uses Escape to close its suggestions first.
            if (e.key === 'Escape' && !e.defaultPrevented) closeLayer();
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [closeLayer]);

    if (typeof document === 'undefined') return null;

    return createPortal(
        <LayerDepthContext.Provider value={layer.depth}>
            <div
                className="fixed inset-0 flex items-end sm:items-center justify-center sm:p-4"
                style={layer.style}
            >
                <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={layer.onClose} aria-hidden />
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label={title}
                    className={`relative w-full ${priceBook ? 'sm:max-w-4xl lg:max-w-6xl md:h-[88vh]' : 'sm:max-w-lg'} max-h-[92dvh] sm:max-h-[88vh] flex flex-col bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden animate-fade-in safe-area-bottom`}
                >
                    {/* Header */}
                    <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 shrink-0">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                            <Icon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <h2 className="text-lg font-bold text-gray-900">{title}</h2>
                        </div>
                        <button
                            type="button"
                            onClick={layer.onClose}
                            className="-mr-1 rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                            aria-label="Close"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    {/* Body — one scroll area on phones; form and Price Book scroll separately on wider screens */}
                    <div
                        data-item-editor-body
                        className={`flex-1 min-h-0 overflow-y-auto ${priceBook ? 'md:overflow-hidden md:grid md:grid-cols-[minmax(0,1fr)_340px] lg:grid-cols-[minmax(0,1fr)_400px] md:grid-rows-[minmax(0,1fr)_auto]' : ''}`}
                    >
                        <div className="p-5 lg:p-6 md:overflow-y-auto md:col-start-1 md:row-start-1">
                            {children}
                            {summary && <div className={priceBook ? 'hidden md:block mt-6' : 'mt-6'}>{summary}</div>}
                        </div>
                        {priceBook && (
                            <div className="border-t border-gray-200 bg-gray-50 md:border-t-0 md:border-l md:col-start-2 md:row-start-1 md:row-span-2 md:min-h-0 md:flex md:flex-col">
                                {priceBook}
                            </div>
                        )}
                        {priceBook && summary && (
                            <div className="md:hidden p-5 border-t border-gray-200">{summary}</div>
                        )}
                        {footer && (
                            <div className="sticky bottom-0 border-t border-gray-200 bg-white px-5 py-3 md:static md:col-start-1 md:row-start-2">
                                {footer}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </LayerDepthContext.Provider>,
        document.body,
    );
}

interface PriceBookPanelProps {
    companyId: string;
    /** Row or "+" tap: fill the form with this item so it can be adjusted before adding. */
    onPick: (item: CatalogItemDTO) => void;
    /** Name currently in the form, so the matching row shows as selected. */
    selectedName?: string;
    /** Inside the phone sheet: the sheet header carries the title and the list fills the rest. */
    inSheet?: boolean;
}

/** Searchable list of the company's Items Catalog. */
export function PriceBookPanel({ companyId, onPick, selectedName, inSheet = false }: PriceBookPanelProps) {
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);

    // Same 'catalog-items' family as the Items Catalog page, so edits there refresh this list.
    const { data, isLoading, isError } = useQuery({
        queryKey: ['catalog-items', companyId, debouncedSearch, 'price-book'],
        queryFn: () => apiService.searchCatalogItems(
            { page: 1, page_size: PRICE_BOOK_PAGE_SIZE, search_text: debouncedSearch || undefined },
            companyId,
        ),
        placeholderData: keepPreviousData,
    });
    const items = data?.items ?? [];

    return (
        <>
            <div className="px-4 pt-4 pb-3 shrink-0">
                {!inSheet && <h3 className="text-base font-semibold text-gray-900 mb-2">Quick Add From Price Book</h3>}
                <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search items..."
                        className={`w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 ${inSheet ? 'text-base' : 'text-sm'}`}
                    />
                </div>
            </div>

            <div className={`px-2 pb-3 ${inSheet ? 'flex-1 min-h-0 overflow-y-auto' : 'md:flex-1 md:min-h-0 md:overflow-y-auto'}`}>
                {isLoading ? (
                    <div className="py-8"><LoadingSpinner /></div>
                ) : isError ? (
                    <p className="px-2 py-8 text-center text-sm text-gray-500">Couldn't load the Price Book.</p>
                ) : items.length === 0 ? (
                    <p className="px-2 py-8 text-center text-sm text-gray-500">
                        {debouncedSearch ? 'No matching items' : 'Your Items Catalog is empty'}
                    </p>
                ) : (
                    <ul className="space-y-0.5">
                        {items.map((item) => {
                            const selected = !!selectedName && item.name === selectedName.trim();
                            return (
                                <li key={item.id}>
                                    <button
                                        type="button"
                                        onClick={() => onPick(item)}
                                        className={`group flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left transition-colors ${selected ? 'bg-primary-50 ring-1 ring-primary-200' : 'hover:bg-white'}`}
                                    >
                                        <span className="min-w-0 flex-1">
                                            <span className="block text-sm font-semibold text-gray-900 truncate">{item.name}</span>
                                            {item.description && (
                                                <span className="block text-xs text-gray-500 line-clamp-1">{item.description}</span>
                                            )}
                                        </span>
                                        <span className="shrink-0 text-sm font-semibold text-gray-900">
                                            ${(item.price ?? 0).toFixed(2)}
                                        </span>
                                        <span
                                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors ${selected
                                                ? 'border-primary-600 bg-primary-600 text-white'
                                                : 'border-primary-200 bg-white text-primary-600 group-hover:bg-primary-50'}`}
                                            aria-hidden
                                        >
                                            {selected ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                                        </span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </>
    );
}

interface BottomSheetProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    /** Tall sheet for lists; otherwise it hugs its content. */
    tall?: boolean;
    children: React.ReactNode;
}

/** Phone sheet that slides up over the item editor, one layer above it. */
export function BottomSheet({ open, onClose, title, tall = false, children }: BottomSheetProps) {
    const layer = useModalLayer({ enabled: open, onClose });
    if (!open || typeof document === 'undefined') return null;

    return createPortal(
        <LayerDepthContext.Provider value={layer.depth}>
            <div className="fixed inset-0 flex items-end sm:items-center sm:justify-center sm:p-4" style={layer.style}>
                <div className="absolute inset-0 bg-black/40" onClick={layer.onClose} aria-hidden />
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label={title}
                    className={`relative w-full flex flex-col bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden animate-fade-in safe-area-bottom ${tall ? 'h-[85dvh] bg-gray-50 sm:max-w-lg' : 'max-h-[70dvh] sm:max-w-sm'}`}
                >
                    {title && (
                        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-200 bg-white shrink-0">
                            <h2 className="text-lg font-bold text-gray-900">{title}</h2>
                            <button
                                type="button"
                                onClick={layer.onClose}
                                className="-mr-1 rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                                aria-label="Close"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                    )}
                    {children}
                </div>
            </div>
        </LayerDepthContext.Provider>,
        document.body,
    );
}

interface OptionSheetProps<T extends string> {
    open: boolean;
    onClose: () => void;
    options: { value: T; label: string }[];
    value: T;
    onSelect: (value: T) => void;
}

/** Short list of choices with a tick on the current one, e.g. the discount type. */
export function OptionSheet<T extends string>({ open, onClose, options, value, onSelect }: OptionSheetProps<T>) {
    return (
        <BottomSheet open={open} onClose={onClose}>
            <ul className="py-2">
                {options.map((option) => (
                    <li key={option.value}>
                        <button
                            type="button"
                            onClick={() => { onSelect(option.value); onClose(); }}
                            className="flex w-full items-center justify-between px-6 py-4 text-left text-base text-gray-900 hover:bg-gray-50 active:bg-gray-100"
                        >
                            {option.label}
                            {option.value === value && <Check className="h-5 w-5 text-primary-600" />}
                        </button>
                    </li>
                ))}
            </ul>
        </BottomSheet>
    );
}
